/**
 * ExtensionRequiredPrompt
 *
 * Shown in the web app when the user needs to subscribe or sign the BAA
 * but the Enter Control Chrome extension is not detected.
 *
 * Logic:
 * - Mobile browser → explain setup must happen on desktop Chrome
 * - Desktop, no ext → link to install the extension, then come back
 *
 * Once the extension is installed it sends EXTENSION_READY, the parent
 * gate re-evaluates, and this screen disappears automatically.
 */

import React from 'react';
import { createLogger } from '@ctrl-enter/tools/utils';
import { isDevelopment } from '../env';
import { Button } from './button';
import { Card } from './card';
import { cn } from '../utils/cn';
import ComputerScreenSvg from '../assets/icons/computer-screen.svg';
import BlueShieldSvg from '../assets/icons/blue-shield.svg';

const logger = createLogger('client:component:extension-required-prompt');

const CWS_EXTENSION_URL =
  'https://chromewebstore.google.com/detail/enter-control/' +
  (import.meta.env.VITE_EXTENSION_ID ?? '');

type Reason = 'subscription' | 'baa' | 'both';

type ExtensionRequiredPromptProps = {
  reason?: Reason;
  isMobile?: boolean;
  className?: string;
};

export const ExtensionRequiredPrompt: React.FC<ExtensionRequiredPromptProps> = ({
  reason = 'both',
  isMobile = false,
  className,
}) => {
  const handleInstall = () => {
    logger.info('🔌 [EXTENSION-REQUIRED] Opening Chrome Web Store install page');
    window.open(CWS_EXTENSION_URL, '_blank', 'noopener,noreferrer');
  };

  const reasonLabel =
    reason === 'subscription'
      ? 'complete your subscription'
      : reason === 'baa'
        ? 'sign the Business Associate Agreement'
        : 'complete subscription setup and sign the BAA';

  return (
    <div
      className={cn('flex flex-col items-center justify-center overflow-y-auto px-4', className)}
    >
      {/* Icon */}
      <div className="mb-4 mt-6 md:mb-6 md:mt-12">
        <img
          src={isMobile ? BlueShieldSvg : ComputerScreenSvg}
          alt="Extension required"
          className="w-20 h-20 md:w-24 md:h-24"
        />
      </div>

      {/* Title */}
      <h1 className="text-lg md:text-xl text-text-one mb-3 text-center">
        {isMobile ? 'Desktop Chrome Required' : 'Extension Required'}
      </h1>

      {/* Subtitle */}
      <p className="text-sm md:text-base text-foreground mb-4 md:mb-6 text-center max-w-md leading-relaxed">
        {isMobile
          ? `To ${reasonLabel}, please open this page in Google Chrome on a desktop computer with the Enter Control extension installed.`
          : `The Enter Control Chrome extension is required to ${reasonLabel}. Once setup is complete you can use the app from any device — including mobile.`}
      </p>

      {/* Card */}
      <Card className="w-full max-w-[540px] p-4 md:p-6 glass-card-bg glass-card-border border border-border-2">
        {isMobile ? (
          <>
            <h3 className="text-base font-bold text-foreground mb-3">How to complete setup:</h3>
            <ol className="space-y-3 text-sm text-foreground list-none">
              <li className="flex items-start gap-2">
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center font-bold">
                  1
                </span>
                Open this page on a desktop computer in <strong>Google Chrome</strong>
              </li>
              <li className="flex items-start gap-2">
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center font-bold">
                  2
                </span>
                Install the <strong>Enter Control</strong> extension from the Chrome Web Store
              </li>
              <li className="flex items-start gap-2">
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center font-bold">
                  3
                </span>
                Complete subscription &amp; BAA — after that, log in from any device
              </li>
            </ol>
          </>
        ) : isDevelopment ? (
          <>
            <h3 className="text-base font-bold text-foreground mb-3">
              Load the dev extension to continue:
            </h3>
            <ol className="space-y-3 text-sm text-foreground list-none">
              <li className="flex items-start gap-2">
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center font-bold">
                  1
                </span>
                Run <code className="bg-muted px-1 rounded">nx dev client-extension</code> to build
              </li>
              <li className="flex items-start gap-2">
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center font-bold">
                  2
                </span>
                Open <strong>chrome://extensions</strong> → enable Developer mode → Load unpacked →
                select <code className="bg-muted px-1 rounded">clients/extension/dist</code>
              </li>
              <li className="flex items-start gap-2">
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center font-bold">
                  3
                </span>
                This page will update automatically once the extension is active
              </li>
            </ol>
          </>
        ) : (
          <>
            <h3 className="text-base font-bold text-foreground mb-3">
              Install the extension to continue:
            </h3>
            <ol className="space-y-3 text-sm text-foreground list-none mb-5">
              <li className="flex items-start gap-2">
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center font-bold">
                  1
                </span>
                Click <strong>Install Extension</strong> below
              </li>
              <li className="flex items-start gap-2">
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center font-bold">
                  2
                </span>
                Add it to Chrome — this page will update automatically
              </li>
              <li className="flex items-start gap-2">
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center font-bold">
                  3
                </span>
                After setup is complete you can log in from any device without the extension
              </li>
            </ol>

            <Button onClick={handleInstall} variant="primary" size="lg" className="w-full">
              Install Extension
              <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                />
              </svg>
            </Button>
          </>
        )}
      </Card>

      <p className="text-xs text-muted-foreground mt-4 md:mt-6 text-center max-w-md mb-4">
        {isMobile
          ? 'Once subscription and BAA are complete, you can use Enter Control from any device.'
          : 'This page will automatically update once the extension is installed and active.'}
      </p>
    </div>
  );
};

ExtensionRequiredPrompt.displayName = 'ExtensionRequiredPrompt';
